/*
 * Copyright (C) 2026 - present Marek Kuzora - All Rights Reserved.
 */


import { Flex } from '@radix-ui/themes';

import { use, useCallback } from 'fierry/react';

import Navigation from 'solis/navigation';
import Routes from 'solis/enum/route';

import Galaxy from 'solis/application/galaxy';

import TimeBadge from 'solis/claude/element/time.badge';

import * as Board from 'solis/react/board';

import Title from 'solis/react/element/title';
import Tile from 'solis/react/element/tile';


const TILE_WIDTH = 340;
const TILE_HEIGHT = 300;


const Section = ({ title, children }) =>
(
  <Flex direction = 'column' gap = '3'>
    <Title>{title}</Title>

    <Flex wrap = 'wrap' gap = '5'>
      {children}
    </Flex>
  </Flex>
);


const onNavigate = (event, dispatch, route) =>
{
  dispatch.pushForeground({ route });
};


const NavTile = ({ name, badge, route, children }) =>
{
  const dispatch = use(Navigation.Dispatch);

  const onClick = useCallback(onNavigate, [ dispatch, route ]);

  return (
    <Tile onClick = {onClick}>
      <Flex direction = 'column' gap = '3'>
        <Flex justify = 'between' align = 'center'>
          <Title>{name}</Title>

          {badge}
        </Flex>

        {children}
      </Flex>
    </Tile>
  );
};


const ActiveBoards = ({ account }) =>
{
  const galaxy = use(Galaxy);

  return account.getBoardViews().values().flatMap(view =>
  {
    const board = galaxy.getBoard(view.cass.id);

    if (board == null)
    {
      return [];
    }

    return (
      <NavTile key = {view.cass.id} name = {board.cass.name} badge = {<TimeBadge date = {board.cass.destroyDate} />} route = {Routes.DashboardBoard.replace(':id', view.cass.id)}>
        <Board.WithBoard board = {board} width = {TILE_WIDTH} height = {TILE_HEIGHT} zoom = {0} settings = {Board.Preview} />
      </NavTile>
    );
  });
};


const YourColonies = ({ account }) =>
{
  const galaxy = use(Galaxy);

  return account.getCurrentColonyViews().values().flatMap(view =>
  {
    const colony = galaxy.getColony(view.cass.colony);

    if (colony == null)
    {
      return [];
    }

    const cass = colony.cass;

    return (
      <NavTile key = {cass.id} name = {colony.getName()} route = {Routes.DashboardColony.replace(':id', cass.id)}>
        <Board.WithColony colony = {colony} width = {TILE_WIDTH} height = {TILE_HEIGHT} zoom = {1} settings = {Board.Preview} />
      </NavTile>
    );
  });
};


const OccupiedSectors = ({ account }) =>
{
  const galaxy = use(Galaxy);

  const currentColonyIDs = new Set(account.getCurrentColonyViews().values().map(view => view.cass.colony));

  const seen = new Set();

  return account.getOriginSectorViews().values().flatMap(view =>
  {
    if (currentColonyIDs.has(view.cass.colony) || seen.has(view.cass.colony))
    {
      return [];
    }

    seen.add(view.cass.colony);

    const colony = galaxy.getColony(view.cass.colony);

    if (colony == null)
    {
      return [];
    }

    const cass = colony.cass;

    return (
      <NavTile key = {cass.id} name = {colony.getName()} route = {Routes.DashboardColony.replace(':id', cass.id)}>
        <Board.WithColony colony = {colony} width = {TILE_WIDTH} height = {TILE_HEIGHT} zoom = {1} settings = {Board.Preview} />
      </NavTile>
    );
  });
};


export default () =>
{
  const account = use(Galaxy).getCurrentAccount();

  return (
    <Flex direction = 'column' gap = '6' p = '5'>

      <Section title = 'Active Boards'>
        <ActiveBoards account = {account} />
      </Section>

      <Section title = 'Your Colonies'>
        <YourColonies account = {account} />
      </Section>

      <Section title = 'Occupied Sectors'>
        <OccupiedSectors account = {account} />
      </Section>

    </Flex>
  );
};
